import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@pantry/ui';
import { useCurrentUser, useUpdateCurrentUser } from '@pantry/api-client';
import { toast } from 'sonner';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { UserIcon } from '@heroicons/react/24/outline';
import { AvatarUpload } from './avatar-upload';

const profileSchema = (t: ReturnType<typeof useLingui>['t']) =>
  z.object({
    name: z
      .string()
      .min(1, t`Name is required`)
      .max(100, t`Name must be 100 characters or less`),
  });

type ProfileFormData = z.infer<ReturnType<typeof profileSchema>>;

export function ProfileSection() {
  const { t } = useLingui();
  const { data: user, isLoading, error } = useCurrentUser();
  const updateUser = useUpdateCurrentUser();
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema(t)),
    defaultValues: {
      name: user?.name || '',
    },
    values: {
      name: user?.name || '',
    },
  });

  const getInitials = () => {
    if (user?.name) {
      return user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const onSubmit = async (data: ProfileFormData) => {
    try {
      await updateUser.mutateAsync({
        name: data.name,
      });
      toast.success(t`Profile updated successfully`);
      setIsEditing(false);
    } catch {
      toast.error(t`Failed to update profile`);
    }
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <Card className="rounded-xl" data-tour="settings-profile">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Trans>Profile</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>Your personal information.</Trans>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-6">
            <div className="h-16 w-16 animate-pulse rounded-xl bg-muted" />
            <div className="flex-1 space-y-4">
              <div className="h-4 w-24 animate-pulse rounded-lg bg-muted" />
              <div className="h-4 w-48 animate-pulse rounded-lg bg-muted" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="rounded-xl" data-tour="settings-profile">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Trans>Profile</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>Your personal information.</Trans>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
            <Trans>Failed to load profile information.</Trans>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl" data-tour="settings-profile">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-display flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Trans>Profile</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>Your personal information.</Trans>
          </CardDescription>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="rounded-xl"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            <Trans>Edit</Trans>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="flex items-start gap-6">
              {/* Avatar upload */}
              <AvatarUpload
                currentAvatarUrl={user?.avatarUrl}
                userName={user?.name}
                userEmail={user?.email}
                size="lg"
              />

              <div className="flex-1 space-y-4">
                {/* Name field */}
                <div className="space-y-2">
                  <Label htmlFor="name">
                    <Trans>Name</Trans>
                  </Label>
                  <Input
                    id="name"
                    {...register('name')}
                    placeholder={t`Your name`}
                    className={`rounded-xl ${errors.name ? 'border-destructive' : ''}`}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive animate-in fade-in slide-in-from-top-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Email (read-only) */}
                <div className="space-y-2">
                  <Label htmlFor="email">
                    <Trans>Email</Trans>
                  </Label>
                  <Input
                    id="email"
                    value={user?.email || ''}
                    disabled
                    className="rounded-xl bg-muted/50"
                  />
                  <p className="text-xs text-muted-foreground">
                    <Trans>
                      Email cannot be changed here. Use the Email section below to update your
                      email.
                    </Trans>
                  </p>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={updateUser.isPending}
                className="rounded-xl"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                <Trans>Cancel</Trans>
              </Button>
              <Button
                type="submit"
                disabled={!isDirty || updateUser.isPending}
                className="rounded-xl"
              >
                {updateUser.isPending ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    <Trans>Saving...</Trans>
                  </div>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    <Trans>Save Changes</Trans>
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <Avatar className="h-16 w-16 rounded-xl">
              <AvatarImage
                src={user?.avatarUrl || undefined}
                alt={user?.name || 'User'}
                className="rounded-xl"
              />
              <AvatarFallback className="text-lg rounded-xl">{getInitials()}</AvatarFallback>
            </Avatar>

            {/* User info */}
            <div className="flex-1 space-y-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  <Trans>Name</Trans>
                </p>
                <p className="text-foreground">{user?.name || t`Not set`}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  <Trans>Email</Trans>
                </p>
                <p className="text-foreground">{user?.email}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
